import { computed, inject, Injectable, signal } from '@angular/core';
import { CustomFactor, CustomFactorDto } from '../types/factor';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { tap } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class FactorService {
  private http = inject(HttpClient);
  private readonly url = `${environment.apiUrl}/factors`;

  private _customFactors = signal<CustomFactor[] | null>(null);
  readonly customFactors = computed(() => this._customFactors() ?? []);

  private loading = false;

  /**
   * Charge la liste uniquement si elle n'est pas encore en mémoire
   */
  loadIfNeeded() {
    if (this._customFactors() || this.loading) return;

    this.loading = true;

    this.http.get<CustomFactor[]>(this.url).subscribe({
      next: (data) => {
        this._customFactors.set(data);
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      },
    });
  }

  /**
   * Force un reload depuis le backend
   */
  reload() {
    this.http
      .get<CustomFactor[]>(this.url)
      .subscribe((data: CustomFactor[]) => {
        this._customFactors.set(data);
      });
  }

  create(data: CustomFactorDto) {
    return this.http.post<CustomFactor>(this.url, data).pipe(
      tap((created) => {
        const current = this._customFactors() ?? [];
        this._customFactors.set([...current, created]);
      }),
    );
  }

  update(data: CustomFactorDto, id: string) {
    return this.http.put<CustomFactor>(`${this.url}/${id}`, data).pipe(
      tap((updated) => {
        const current = this._customFactors() ?? [];
        this._customFactors.set(
          current.map((f) => (f.id === id ? updated : f)),
        );
      }),
    );
  }

  delete(id: string) {
    return this.http.delete<void>(`${this.url}/${id}`).pipe(
      tap(() => {
        const current = this._customFactors() ?? [];
        this._customFactors.set(current.filter((f) => f.id !== id));
      }),
    );
  }
}
